/**
 * Centralized main navigation configuration
 *
 * This file contains the icon, title, and href configuration for all main nav tabs.
 * Used by both the desktop Navbar and MobileDrawer to avoid duplication.
 */
import type { IconDefinition } from "@fortawesome/pro-regular-svg-icons";

import {
	faCalendarCheck,
	faPaperPlane,
	faRankingStar,
	faRectangleList,
} from "@performyard/fontawesome/pro-regular-svg-icons";
import { faChartSimple, faGears, faGlobe, faHeartPulse, faUserTie } from "@performyard/fontawesome/pro-solid-svg-icons";

import type { NavigationConfig } from "../../api/services/defs/navigation";

// Re-export NavigationConfig for convenience
export type { NavigationConfig };

/**
 * Recursive navigation item structure.
 * Can be nested arbitrarily deep for multi-level navigation.
 *
 * @property id - Unique identifier matching route's navPath segment
 * @property title - Display text
 * @property href - Target URL
 * @property children - Optional child nav items (recursive)
 */
export interface NavItemConfig {
	id: string;
	title: string;
	href: string;
	children?: NavItemConfig[];
}

/**
 * Top-level main nav item with icon.
 * Extends NavItemConfig with icon for primary navigation bar.
 *
 * @property icon - FontAwesome icon definition
 */
export interface MainNavItemConfig extends NavItemConfig {
	icon: IconDefinition;
}

/**
 * Helper function to traverse nav tree and find item by path.
 * Supports arbitrary depth for multi-level navigation.
 *
 * @param items - Array of nav items to search
 * @param path - Array of IDs representing the path (e.g., ["performance", "dashboard"])
 * @returns The nav item at the end of the path, or null if not found
 *
 * @example
 * // Find sub-nav item
 * findNavItemByPath(mainNavItems, ["performance", "dashboard"])
 *
 * // Find 3rd level item (future use)
 * findNavItemByPath(mainNavItems, ["performance", "reviews", "pending-reviews"])
 */
export function findNavItemByPath(items: NavItemConfig[], path: (string | null | undefined)[]): NavItemConfig | null {
	// Filter out null/undefined from path
	const validPath = path.filter((segment): segment is string => segment != null);

	if (validPath.length === 0) return null;

	// Find the first item in the current level
	const currentId = validPath[0];
	const currentItem = items.find((item) => item.id === currentId);

	if (!currentItem) return null;

	// If this is the last segment, return the item
	if (validPath.length === 1) return currentItem;

	// Otherwise, recurse into children
	if (!currentItem.children) return null;
	return findNavItemByPath(currentItem.children, validPath.slice(1));
}

/**
 * Main navigation configuration builder.
 * Constructs nav tree from server's visibleNavTags.
 *
 * @param config - Navigation configuration from server
 * @param theUserId - User ID for URL interpolation
 * @returns Array of top-level nav items with nested children
 */
export const calculateMainNavConfig = (config: NavigationConfig, theUserId: string): MainNavItemConfig[] => {
	const visibleNavTags = config.visibleNavTags || {};
	const mainNavConfig: MainNavItemConfig[] = [];

	// Performance section - show if key exists
	if (visibleNavTags["performance"] !== undefined) {
		const performanceTabs: MainNavItemConfig = {
			id: "performance",
			icon: faUserTie,
			title: "Dashboard",
			href: "/",
			children: [],
		};
		if (visibleNavTags["performance"]?.includes("dashboard")) {
			performanceTabs.children?.push({
				id: "dashboard",
				title: "Dashboard",
				href: `/employee-page/${theUserId}`,
			});
		}
		if (visibleNavTags["performance"]?.includes("reviews")) {
			performanceTabs.children?.push({
				id: "reviews",
				title: "Reviews",
				href: `/reviews/${theUserId}/`,
			});
		}
		if (visibleNavTags["performance"]?.includes("goals")) {
			performanceTabs.children?.push({
				id: "goals",
				title: "Goals",
				href: `/employees/${theUserId}/goals/?filters=%7B"status"%3A%5B%7B"value"%3A"active"%2C"label"%3A"Active"%7D%5D%7D`,
			});
		}
		if (visibleNavTags["performance"]?.includes("feedback")) {
			performanceTabs.children?.push({
				id: "feedback",
				title: "Feedback",
				href: `/feedback/${theUserId}/received`,
			});
		}
		if (visibleNavTags["performance"]?.includes("documents")) {
			performanceTabs.children?.push({
				id: "documents",
				title: "Documents",
				href: `/documents/${theUserId}`,
			});
		}
		mainNavConfig.push(performanceTabs);
	}

	// Company section - show if key exists
	if (visibleNavTags["company"] !== undefined) {
		const companyTabs: MainNavItemConfig = {
			id: "company",
			icon: faGlobe,
			title: "Company",
			href: "/directory/",
			children: [],
		};
		if (visibleNavTags["company"]?.includes("directory")) {
			companyTabs.children?.push({
				id: "directory",
				title: "Directory",
				href: "/directory",
			});
		}
		if (visibleNavTags["company"]?.includes("org-chart")) {
			companyTabs.children?.push({
				id: "org-chart",
				title: "Org Chart",
				href: "/org-chart",
			});
		}
		if (visibleNavTags["company"]?.includes("reviews")) {
			companyTabs.children?.push({
				id: "reviews",
				title: "Reviews",
				href: "/company/reviews/cycles/",
			});
		}
		// Goals with sub-navigation - check if goals key exists as top-level
		if (visibleNavTags["goals"] !== undefined && visibleNavTags["goals"].length > 0) {
			const goalsNav: NavItemConfig = {
				id: "goals",
				title: "Goals",
				href: `/company/goals/${visibleNavTags["goals"][0]}/`,
				children: [],
			};

			if (visibleNavTags["goals"]?.includes("list")) {
				goalsNav.children?.push({
					id: "list",
					title: "List",
					href: "/company/goals/list",
				});
			}
			if (visibleNavTags["goals"]?.includes("dashboard")) {
				goalsNav.children?.push({
					id: "dashboard",
					title: "Overview",
					href: "/company/goals/dashboard",
				});
			}
			if (visibleNavTags["goals"]?.includes("alignment")) {
				goalsNav.children?.push({
					id: "alignment",
					title: "Alignment",
					href: "/company/goals/alignment",
				});
			}
			if (visibleNavTags["goals"]?.includes("employees-without-goals")) {
				goalsNav.children?.push({
					id: "employees-without-goals",
					title: "Employees Without Goals",
					href: "/company/goals/employees-without-goals",
				});
			}

			companyTabs.children?.push(goalsNav);
		}
		if (visibleNavTags["company"]?.includes("feedback")) {
			companyTabs.children?.push({
				id: "feedback",
				title: "Feedback",
				href: "/company/feedback/",
			});
		}
		if (visibleNavTags["company"]?.includes("workflows")) {
			companyTabs.children?.push({
				id: "workflows",
				title: "Workflows",
				href: "/company/workflows",
			});
		}
		if (visibleNavTags["company"]?.includes("meetings")) {
			companyTabs.children?.push({
				id: "meetings",
				title: "Meetings",
				href: "/company/meetings",
			});
		}
		if (visibleNavTags["company"]?.includes("resources")) {
			companyTabs.children?.push({
				id: "resources",
				title: "Resources",
				href: "/company/resources/",
			});
		}
		mainNavConfig.push(companyTabs);
	}

	// Engagement section - show if key exists
	if (visibleNavTags["engagement"] !== undefined) {
		const engagementTabs: MainNavItemConfig = {
			id: "engagement",
			icon: faHeartPulse,
			title: "Engagement",
			href: "/engagement/overview/",
			children: [],
		};
		if (visibleNavTags["engagement"]?.includes("overview")) {
			engagementTabs.children?.push({
				id: "overview",
				title: "Overview",
				href: "/engagement/overview",
			});
		}
		if (visibleNavTags["engagement"]?.includes("cohorts")) {
			engagementTabs.children?.push({
				id: "cohorts",
				title: "Cohorts",
				href: "/engagement/cohorts",
			});
		}
		if (visibleNavTags["engagement"]?.includes("factors")) {
			engagementTabs.children?.push({
				id: "factors",
				title: "Factors",
				href: "/engagement/factors",
			});
		}
		if (visibleNavTags["engagement"]?.includes("questions")) {
			engagementTabs.children?.push({
				id: "questions",
				title: "Questions",
				href: "/engagement/questions",
			});
		}
		if (visibleNavTags["engagement"]?.includes("enps")) {
			engagementTabs.children?.push({
				id: "enps",
				title: "eNPS",
				href: "/engagement/enps",
			});
		}
		mainNavConfig.push(engagementTabs);
	}

	// Meeting workspaces - show if key exists
	if (visibleNavTags["meeting-workspaces"] !== undefined) {
		mainNavConfig.push({
			id: "meeting-workspaces",
			icon: faCalendarCheck,
			title: "Meetings",
			href: "/meeting-workspaces/",
		});
	}

	// Surveys - show if key exists
	if (visibleNavTags["surveys"] !== undefined) {
		mainNavConfig.push({
			id: "surveys",
			icon: faPaperPlane,
			title: "Surveys",
			href: "/surveys/",
		});
	}

	// Analytics - show if key exists
	if (visibleNavTags["analytics"] !== undefined || visibleNavTags["reporting"] !== undefined) {
		mainNavConfig.push({
			id: "analytics",
			icon: faRectangleList,
			title: "Reporting & Analytics",
			href: "/analytics/",
		});
	}

	// Reporting - show if key exists
	if (visibleNavTags["reporting"] !== undefined) {
		mainNavConfig.push({
			id: "reporting",
			icon: faChartSimple,
			title: "Reporting",
			href: "/reporting/",
		});
	}

	// Compensation - show if key exists
	if (visibleNavTags["compensation"] !== undefined) {
		mainNavConfig.push({
			id: "compensation",
			icon: faRankingStar,
			title: "Compensation",
			href: "/compensation/",
		});
	}

	// Administration - show if key exists
	if (visibleNavTags["administration"] !== undefined) {
		mainNavConfig.push({
			id: "administration",
			icon: faGears,
			title: "Administration",
			href: "/administration/",
		});
	}

	return mainNavConfig;
};

/**
 * Check if a tab should show promotional "NEW" badge
 *
 * @param tabId - The tab identifier
 * @param config - Navigation configuration with feature flags and promotion info
 * @returns Whether to show promotional badge
 */
export function shouldShowPromotionalAd(
	tabId: string,
	config: {
		isAdmin: boolean;
		isBasicAdmin: boolean;
		isSurveysEnabled: boolean;
		isMeetingsEnabled: boolean;
		isEngagementEnabled: boolean;
		currentPromotion: Record<string, unknown>;
	}
): boolean {
	const hasAdminAccess = config.isAdmin || config.isBasicAdmin;
	const currentPromotion = config.currentPromotion || {};
	const promotionType = (currentPromotion as { type?: string }).type;

	if (!hasAdminAccess || !promotionType) return false;

	switch (tabId) {
		case "surveys":
			return !config.isSurveysEnabled && promotionType === "surveys";

		case "meeting-workspaces":
			return !config.isMeetingsEnabled && promotionType === "meetings";

		case "engagement":
			return !config.isEngagementEnabled && promotionType === "engagement";

		default:
			return false;
	}
}

/**
 * Get promotional URL for a tab with NEW badge
 *
 * @param tabId - The tab identifier
 * @param email - User's email for tracking
 * @returns URL to promotional page
 */
export function getPromotionalUrl(tabId: string, email = "", promotionId = ""): string {
	const baseUrl = "https://www.performyard.com";
	const source = "icon";
	const promotionParam = promotionId ? `&promotionId=${promotionId}` : "";

	switch (tabId) {
		case "surveys":
			return `${baseUrl}/performyard-surveys/?email=${email}&source=${source}${promotionParam}`;

		case "meeting-workspaces":
			return `${baseUrl}/performyard-meetings/?email=${email}&source=${source}${promotionParam}`;

		case "engagement":
			return `${baseUrl}/performyard-engagement/?email=${email}&source=${source}${promotionParam}`;

		default:
			return "#";
	}
}
